// Imports => MOBX
import { observable, computed, makeObservable, action, toJS } from 'mobx';
import { AcBuildURLSearchParams, AcMapPublication } from '@utils';
import { CONDUCTION_KEYS, LABELS, LABELS_DYNAMIC } from '@constants';

let app = {};

const LIMIT = 100;

export const DEFAULT_SEARCH_QUERY = {
  _limit: LIMIT,
};

const DEFAULT_QUERY = {};

if (process.env.API_URL_COMMONGROUND_ORGANIZATION_OIN) {
}

// OpenRegister levert facetten in twee vormen, afhankelijk van de
// omgevingsversie (zie AS-23164):
//  - nieuw (gouda.accept):        facets.facets["@self"].schema.buckets
//                                 met {key:server-id, label:naam, results}
//  - oud / 3-juli-stable          facets._schema.data.buckets
//    (acato.accept, tilburg):     met {value:server-id, label:naam, count}
// Beide worden genormaliseerd naar {key:naam, value:server-id, results} zoals
// de componenten verwachten. Zo blijft Gouda werken en breekt het niet zodra
// de omgeving naar de stable wordt gemigreerd (en andersom).
const acFacetBuckets = (facet) => facet?.buckets ?? facet?.data?.buckets ?? [];

const acNormalizeBuckets = (facet) =>
  acFacetBuckets(facet).map((bucket) => ({
    key: bucket.label ?? bucket.key ?? bucket.value,
    value: bucket.key ?? bucket.value,
    results: bucket.results ?? bucket.count ?? 0,
  }));

// Facet-knooppunt ophalen ongeacht de responsevorm (nieuw of oud).
const acCategoryFacet = (facets) => facets?.facets?.['@self']?.schema ?? facets?._schema;

const acThemeFacet = (facets) => facets?.facets?.thema ?? facets?.thema;

export class PublicationsStore {
  constructor(store) {
    app.store = store;
    makeObservable(this);
  }

  @observable
  mobileFiltersOpen = false;

  @observable
  items = [];

  @observable
  latest_items = [];

  @observable
  single = null;

  @observable
  attachments = null;

  @observable
  categories = [];

  @observable
  categoryFacets = [];

  @observable
  themes = [];

  @observable
  themesFacets = [];

  @observable
  pagination = {};

  @observable
  attachmentPagination = {
    page: 1,
    perPage: 10,
  };

  @observable
  defaultQuery = DEFAULT_QUERY;

  @observable
  aggregationsQuery = {
    _facetable: 'true',
    '_facets[@self][schema][type]': 'terms',
    '_facets[thema][type]': 'terms',
  };

  @observable
  query = DEFAULT_SEARCH_QUERY;

  @observable
  loading = {
    status: false,
    message: undefined,
  };

  @observable
  loading_latest = {
    status: false,
    message: undefined,
  };

  @observable
  attachmentSearch = '';

  @computed
  get all_themes() {
    return this.themes;
  }

  @computed
  get all_categories() {
    return this.categories;
  }

  @computed
  get all_category_facets() {
    return this.categoryFacets;
  }

  @computed
  get categories_with_facets() {
    return this.categories.map((category) => {
      delete category.results;
      const facet = this.categoryFacets.find((facet) => facet.key === category.key);
      return {
        ...category,
        results: facet?.results || 0,
      };
    });
  }

  @action
  getFilteredAttachments = (primary = false, page) => {
    const isPrimary = (attachment) => {
      const { labels } = attachment;

      if (!labels || !labels.length) {
        return false;
      }

      if (labels.includes('bijlage')) {
        return false;
      }

      return true;
    };

    // First filter by primary/secondary (based on labels)
    const filteredAttachmentsLabel = this.attachments?.filter((attachment) => {
      // Primary attachments should contain a label, unless there is a label called 'bijlage'
      return primary ? isPrimary(attachment) : !isPrimary(attachment);
    });


    // Then filter by search term if one exists
    const searchFiltered = this.attachmentSearch
      ? filteredAttachmentsLabel?.filter((attachment) => {
          return attachment?.title?.toLowerCase().includes(this.attachmentSearch.toLowerCase());
        })
      : filteredAttachmentsLabel;

    // De API levert bijlagen ongeordend aan; sorteer alfabetisch op titel
    // (hoofdletter-ongevoelig, natuurlijke volgorde zodat "Bijlage 2" vóór
    // "Bijlage 10" komt) voor voorspelbaarheid en doorzoekbaarheid.
    const sorted = searchFiltered
      ?.slice()
      .sort((a, b) => (a?.title || '').localeCompare(b?.title || '', 'nl', { numeric: true, sensitivity: 'base' }));

    if (page) {
      const start = (page - 1) * this.attachmentPagination.perPage;
      const end = start + this.attachmentPagination.perPage;
      return sorted?.slice(start, end);
    }

    return sorted;
  };

  @action
  setAttachmentsPage = (page) => {
    this.attachmentPagination.page = page;
  };

  @action
  fetchLatestPublications = async (limit = 3) => {
    this.loading_latest.status = true;

    try {
      // First fetch featured publications
      const featuredQuery = {
        ...DEFAULT_QUERY,
        _limit: 3, // Fetch enough to sort later
        _order: { publicatiedatum: 'desc' },
      };

      // Fetch all recent publications as well (for fallback)
      const recentQuery = {
        ...DEFAULT_QUERY,
        _limit: 3, // Fetch enough to fill gaps
        _order: { publicatiedatum: 'desc' },
      };

      const [featuredResponse, recentResponse] = await Promise.all([
        app.store.api.publications.search(featuredQuery),
        app.store.api.publications.search(recentQuery),
      ]);

      // Get the featured publications (already sorted by date desc)
      const featuredItems = featuredResponse.results || [];

      // Get all recent publications
      const recentItems = recentResponse.results || [];

      let finalItems = [];

      if (featuredItems.length >= limit) {
        // If we have enough featured items, just take the most recent ones
        finalItems = featuredItems.slice(0, limit);
      } else if (featuredItems.length > 0) {
        // If we have some featured items but not enough, add recent non-featured items
        const nonFeaturedItems = recentItems.filter(
          (item) => !item.featured && !featuredItems.some((featured) => featured.id === item.id)
        );

        finalItems = [...featuredItems, ...nonFeaturedItems.slice(0, limit - featuredItems.length)];
      } else {
        // Fallback: If no featured items, use most recent
        finalItems = recentItems.slice(0, limit);
      }

      this.setLatestItems(finalItems);
    } catch (e) {
      // Set empty array on error to avoid UI issues
      this.setLatestItems([]);
    } finally {
      this.loading_latest.status = false;
    }
  };

  @computed
  get all_theme_facets() {
    return this.themesFacets;
  }

  @computed
  get search_query() {
    return { ...this.defaultQuery, ...this.aggregationsQuery, ...this.query };
  }

  get aggregations_query() {
    return { ...this.defaultQuery, ...this.aggregationsQuery };
  }

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @computed
  get is_loading_latest() {
    return this.loading_latest.status;
  }

  @computed
  get get_order() {
    return this.query._order;
  }

  @computed
  get get_single() {
    if (!this.single) {
      return null;
    }

    return AcMapPublication(this.single);
  }

  @computed
  get all_publications() {
    return this.items.map((item) => AcMapPublication(item));
  }

  @computed
  get latest_publications() {
    return this.latest_items
      ? this.latest_items.map((item) => {
          return AcMapPublication(item);
        })
      : [];
  }

  @action
  setItems = (items) => {
    this.items = items;
  };

  @action
  setLatestItems = (items) => {
    this.latest_items = items;
  };

  @action
  setAttachments = (attachments) => {
    this.attachments = attachments;
  };

  @action
  setPagination = (pagination) => {
    this.pagination = pagination;
  };

  @action
  setAttachmentPagination = (pagination) => {
    this.attachmentPagination = pagination;
  };

  @action
  category_checked = (id) => {
    return this.query?.[CONDUCTION_KEYS.CATEGORY]?.includes(id);
  };

  @action
  theme_checked = (id) => {
    return this.query?.[CONDUCTION_KEYS.THEMES]?.includes(id);
  };

  @action
  setQueryDate = (key, value) => {
    if (!this.query.publicatiedatum) {
      this.query.publicatiedatum = {};
    }

    this.setPage(1);
    this.query.publicatiedatum[key] = value;
  };

  @action
  updateQuery = (query) => {
    this.query = { ...this.query, ...query };
  };

  @action
  setSearchQuery = (searchQuery) => {
    this.query._search = searchQuery;
  };

  @action
  setPage = (page) => {
    this.query._page = page;
    this.pagination.page = page;
  };

  @action
  resetSort = () => {
    const newObject = { ...this.query };
    delete newObject._order;
    this.query = newObject;
  };

  @action
  setSort = (key, value) => {
    this.query._order = {};
    this.query._order[key] = value;
  };

  @action
  toggleSearchArrayValue = (key, value) => {
    if (!this.query[key]) {
      this.query[key] = [];
    }

    const index = this.query[key]?.indexOf(value);
    // Remove item if we find it in the array.
    if (index !== -1) {
      this.query[key] = this.query[key].filter((cat) => cat !== value);
      return;
    }

    if (key === CONDUCTION_KEYS.CATEGORY) {
      this.setPage(1);
    }

    this.query[key] = [...this.query[key], value];
  };

  @action
  setLoadingStatus = (status) => {
    this.loading.status = status;
  };

  @action
  setPublication = (publication) => {
    this.single = publication;
  };

  @action
  setThemes = (themes) => {
    this.themes = themes;
  };

  @action
  setCategories = (categories) => {
    this.categories = categories;
  };

  @action
  setCategoryFacets = (categoryFacets) => {
    this.categoryFacets = categoryFacets;
  };

  @action
  setThemesFacets = (themesFacets) => {
    this.themesFacets = themesFacets;
  };

  @action
  toggleMobileFilters = () => {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  };

  @action
  getSearchPageURL = (params = null) => {
    const urlParams = AcBuildURLSearchParams(params ?? this.query);
    if (!urlParams) {
      return '/zoeken';
    }
    return `/zoeken?${urlParams}`;
  };

  // Vertaalt de UI-query (categorienamen, publicatiedatum-object) naar
  // server-params; /zoeken-URLs en componentwaarden blijven zo ongewijzigd.
  toServerQuery = (query) => {
    const {
      [CONDUCTION_KEYS.CATEGORY]: categoryNames,
      [CONDUCTION_KEYS.THEMES]: themes,
      publicatiedatum,
      ...rest
    } = query;
    const params = { ...rest };

    if (categoryNames?.length) {
      // Serverfilter verwacht (omgevings-specifieke) schema-id's; vertaal de
      // categorienamen via de geladen facet-buckets.
      const ids = categoryNames
        .map((name) => this.categories.find((bucket) => bucket.key === name)?.value)
        .filter((id) => id !== undefined);
      if (ids.length) params['@self[schema]'] = ids;
    }

    if (themes?.length) {
      params[CONDUCTION_KEYS.THEMES] = themes;
    }

    if (publicatiedatum?.gte) params['publicatiedatum[gte]'] = publicatiedatum.gte;
    if (publicatiedatum?.lte) params['publicatiedatum[lte]'] = publicatiedatum.lte;

    return params;
  };

  @action
  fetchPublications = async () => {
    this.loading.status = true;

    app.store.api.publications
      .search(this.toServerQuery(this.search_query))
      .then((response) => {
        this.setCategoryFacets(acNormalizeBuckets(acCategoryFacet(response.facets)));
        this.setThemesFacets(acNormalizeBuckets(acThemeFacet(response.facets)));

        this.setItems(response.results);
        delete response.results;
        this.setPagination(response);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  fetchPublication = async (_id) => {
    this.loading.status = true;

    // De embedded attachments-array op de publicatie is deprecated; het
    // attachments-endpoint is de bron en levert size/labels direct mee.
    Promise.all([
      app.store.api.publications.single(_id),
      app.store.api.publications.attachments(_id).catch(() => ({ results: [] })),
    ])
      .then(([response, attachmentsResponse]) => {
        this.setPublication({
          ...response,
          attachments: attachmentsResponse?.results || [],
        });
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  resetPublication = () => {
    this.single = null;
  };

  @action
  fetchAttachments = async (_id) => {
    this.loading.status = true;

    app.store.api.publications
      .attachments(_id)
      .then((response) => {
        this.setAttachments(response.results);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  resetAttachments = () => {
    this.attachments = null;
  };

  @action
  resetSearchQuery = () => {
    this.query = DEFAULT_SEARCH_QUERY;
  };

  @action
  resetAggregations = () => {
    this.categories = [];
    this.themesFacets = [];
  };

  @action
  fetchAggregations = async () => {
    this.loading.status = true;
    // Promise-chain teruggeven zodat de aanroeper op de facets kan wachten
    // (categorie-buckets zijn nodig voor de naam→schema-id-vertaling in
    // toServerQuery; zonder facets filtert een categorie-deeplink niet).
    return app.store.api.publications
      .searchAggregations({ ...this.aggregations_query, _limit: 0 })
      .then((response) => {
        this.setCategories(acNormalizeBuckets(acCategoryFacet(response.facets)));
        this.setThemes(acNormalizeBuckets(acThemeFacet(response.facets)));
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setLoadingStatus(false);
      });
  };

  @action
  setAttachmentSearch = (search) => {
    this.attachmentSearch = search;
    this.attachmentPagination.page = 1; // Reset to first page when searching
  };
}

export default PublicationsStore;
